<template>
  <div
    class="relative inline-flex w-full rounded-[24px] items-end px-8 transition-colors duration-300 ease-in-out"
    :class="withSuggestions ? 'bg-static-default-low' : 'bg-static-default-mid'"
  >
    <!-- Screen reader only label -->
    <label class="sr-only" for="auto-resize-textarea">{{ label }}</label>

    <!-- Auto-resize Textarea -->
    <div
      ref="textareaContainer"
      class="grid w-full after:invisible after:min-h-[calc(var(--textarea-rows)*1.5em_+_24px)] after:overflow-hidden after:whitespace-pre-wrap after:px-16 after:py-12 after:text-inherit after:content-[attr(data-cloned-val)_'_'] after:line-clamp-[--textarea-rows] after:[grid-area:1/1/2/2] not-supports-[line-clamp-1]:after:max-h-[calc(var(--textarea-rows)*1.5em_+_24px)]"
      data-cloned-val=""
    >
      <textarea
        id="auto-resize-textarea"
        ref="textarea"
        class="w-full resize-none overflow-y-auto bg-transparent outline-none px-8 py-12 [grid-area:1/1/2/2] scrollbar-none placeholder:text-static-default-hi-disabled align-content-center-safe"
        :placeholder="label"
        :rows="rowsRef"
        :value
        v-on="textareaState.listeners"
        @keydown="handleKeydown"
      />
    </div>

    <RevButtonIcon
      v-if="!!value"
      :aria-label="buttonLabel"
      class="mb-8 bg-action-ai-hi"
      :icon="IconArrowUp"
      size="small"
      type="submit"
      variant="primary"
    />
  </div>
</template>

<script setup lang="ts">
import { type Ref, nextTick, onMounted, ref, toRef } from 'vue'

import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { useNativeInput } from '@ds/composables/useNativeInput'
import { IconArrowUp } from '@ds/icons/IconArrowUp'
import type { InputValue } from '@ds/types/InputValue'
import { requestAnimationFrame } from '@ds/utils/requestAnimationFrame'
import { watchThrottled } from '@vueuse/core'

const props = defineProps<{
  disabled?: boolean
  label: string
  modelValue: InputValue
  buttonLabel: string
  withSuggestions?: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: InputValue]
  input: [value: Event]
  submit: []
}>()

const ROW_HEIGHT = 24
// py-12 = 12px top + 12px bottom
const TEXTAREA_PADDING = 24
const MAX_ROWS = 3

const rowsRef = ref(1)
const textareaContainer = ref<HTMLDivElement | null>(null)
const textarea = ref<HTMLInputElement | HTMLTextAreaElement | null>(null)
const value = toRef(props, 'modelValue')

const syncClonedContentWithPseudoElement = (clonedValue: string) => {
  if (!textareaContainer.value) return
  textareaContainer.value.setAttribute('data-cloned-val', clonedValue)
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key !== 'Enter') return
  if (event.shiftKey) return
  const isTouchPrimary = window.matchMedia(
    '(hover: none) and (pointer: coarse)',
  ).matches
  if (isTouchPrimary && !event.metaKey && !event.ctrlKey) return
  event.preventDefault()
  emit('submit')
}

const textareaState = useNativeInput(
  textarea as Ref<HTMLInputElement | HTMLTextAreaElement | null>,
  value,
  {
    onInput: (event) => {
      emit('update:modelValue', event.target.value)
      syncClonedContentWithPseudoElement(event.target.value)
      emit('input', event)
    },
  },
)

function setHeightBasedOnRows(rowsForContent: number) {
  rowsRef.value = Math.min(rowsForContent || 1, MAX_ROWS)

  requestAnimationFrame(() => {
    textareaContainer.value?.style.setProperty(
      '--textarea-rows',
      rowsRef.value.toString(),
    )
  })
}

async function resize() {
  if (!value.value) {
    setHeightBasedOnRows(1)

    return
  }

  // Temporarily collapse the textarea to 1 row in order to read its natural scrollHeight
  rowsRef.value = 1
  textareaContainer.value?.style.setProperty('--textarea-rows', '1')

  // Ensure Vue has flushed before reading scrollHeight
  await nextTick()

  const textAreaScrollHeight =
    textarea.value?.scrollHeight || ROW_HEIGHT + TEXTAREA_PADDING

  const rowsCalc = (textAreaScrollHeight - TEXTAREA_PADDING) / ROW_HEIGHT

  setHeightBasedOnRows(rowsCalc)
}

watchThrottled(
  value,
  () => {
    resize()
  },
  {
    immediate: true,
    flush: 'post',
    throttle: 200,
  },
)

onMounted(() => {
  syncClonedContentWithPseudoElement(String(value.value || ''))

  if (textarea.value) {
    textarea.value.focus()
  }
})
</script>
